import apiClient from "./apiClient";
import User from "../models/User";
import Organisation from "../models/Organisation";
import AppError from "../utils/AppError";
import axios from "axios";

const buildUserAndOrganisation = (rawUser) => {
  const user = new User({
    id: rawUser.id,
    lastLogin: rawUser.lastLogin,
    isAdmin: rawUser.isAdmin,
    isStaff: rawUser.isStaff,
    isMember: rawUser.isMember,
    isServiceUser: rawUser.isServiceUser,
    email: rawUser.email,
    firstName: rawUser.firstName,
    lastName: rawUser.lastName,
    group: rawUser.group,
    fullName: rawUser.fullName,
    isActive: rawUser.isActive,
    requireTfa: rawUser.requireTfa,
    userLevel: rawUser.userLevel,
    createdAt: rawUser.createdAt,
    organisation: rawUser.organisation,
    oid: rawUser.oid,
    membershipCount: rawUser.memberships?.length ?? 0,
  });

  const primaryOrg =
    rawUser.organisation ||
    rawUser.memberships?.find((m) => m.primary)?.organisation ||
    rawUser.memberships?.[0]?.organisation ||
    null;
  const organisation = primaryOrg
    ? new Organisation({
        organisationId: primaryOrg.id,
        organisationType: primaryOrg.orgType,
        organisationName: primaryOrg.fullName,
        orgCountryCode: primaryOrg.country,
        // Normalise `country` -> `countryCode` so linked orgs render against the
        // same field key the org switchers already use for their flags/rows.
        linkedOrganisations: (primaryOrg.linkedOrganisations || []).map((o) => ({
          ...o,
          countryCode: o.countryCode ?? o.country ?? null,
        })),
      })
    : null;

  return { user, organisation };
};

const AuthService = {
  async login({ email, password }) {
    try {
      const { data } = await axios.post("/api/v3/auth/login", {
        email,
        password,
      });
      const { user, organisation } = buildUserAndOrganisation(data.user);
      return {
        accessToken: data.access,
        refreshToken: data.refresh,
        user,
        organisation,
      };
    } catch (error) {
      throw new AppError({
        title: "Login Error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          error?.response?.data?.errorMessage ||
          "An error occurred during login. Please try again.",
      });
    }
  },

  async checkEmailStatus(email) {
    try {
      const { data } = await axios.post("/api/v3/auth/email-status", {
        email,
      });
      const org = data.organisation || null;
      return {
        status: data.status,
        organisation: org
          ? {
              id: org.id,
              name: org.name,
              shortName: org.shortName,
              logoUrl: org.logoUrl ?? null,
            }
          : null,
      };
    } catch (error) {
      if (error.response?.status === 429) {
        throw new AppError({
          title: "Too many attempts",
          response: error.response,
          userMessage:
            "Too many sign-in attempts. Please wait a minute and try again.",
        });
      }
      throw new AppError({
        title: "Email lookup error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          "Could not check that email. Please try again.",
      });
    }
  },

  // PATCH /users/link-ms-account is special: it's authenticated by the MS
  // bearer, not the SimpleJWT. We deliberately bypass apiClient (which would
  // attach the JWT) and use raw axios with an explicit Authorization header.
  async linkMsAccount({ msAccessToken, email }) {
    try {
      const { data } = await axios.patch(
        "/api/v3/users/link-ms-account",
        { email },
        { headers: { Authorization: `Bearer ${msAccessToken}` } }
      );
      return data;
    } catch (error) {
      // Preserve the raw response so the caller can surface the server's
      // display title/description in the link-result modal.
      const data = error.response?.data || {};
      throw new AppError({
        title: "Link Microsoft account",
        response: error.response,
        userMessage:
          data.description ||
          data.error ||
          "Could not link your Microsoft account. Please try again.",
      });
    }
  },

  async me() {
    try {
      const { data } = await apiClient.get("/auth/me");
      return buildUserAndOrganisation(data.user);
    } catch (error) {
      throw new AppError({
        title: "Session error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          "Could not load your session. Please sign in again.",
      });
    }
  },

  async refreshToken(refreshToken) {
    try {
      const payload = { refresh: refreshToken };
      const { data } = await apiClient.post("/auth/token/refresh", payload);
      return data.access;
    } catch (error) {
      throw new AppError({
        title: "Token Refresh Error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          "Unable to refresh access token. Please log in again.",
      });
    }
  },
  async resetPassword(email) {
    try {
      const { data } = await axios.post("/api/v3/auth/reset-password", {
        email,
      });
      return data;
    } catch (error) {
      throw new AppError({
        title: "Reset Password Error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          "Could not initiate password reset process.",
      });
    }
  },
  async changePassword(uid, token, newPassword, newPasswordConfirm) {
    try {
      const { data } = await axios.post("/api/v3/auth/reset-password/confirm", {
        uid,
        token,
        newPassword,
        newPasswordConfirm,
      });
      return data;
    } catch (error) {
      throw new AppError({
        title: "Change Password Error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          "Could not change password. Please try again.",
      });
    }
  },
  async register(payload) {
    try {
      const { data } = await axios.post("/api/v3/auth/register", payload);
      return data;
    } catch (error) {
      const errorData = error.response?.data || {};

      const formatFieldErrors = (data) => {
        const fieldNames = {
          firstName: "First Name",
          lastName: "Last Name",
          password: "Password",
          email: "Email",
        };

        const errors = [];

        Object.keys(fieldNames).forEach((field) => {
          if (data[field] && Array.isArray(data[field])) {
            const fieldDisplayName = fieldNames[field];
            data[field].forEach((message) => {
              errors.push(`${fieldDisplayName}: ${message}`);
            });
          }
        });

        if (data.nonFieldErrors && Array.isArray(data.nonFieldErrors)) {
          data.nonFieldErrors.forEach((message) => {
            errors.push(message);
          });
        }

        if (data.error) {
          errors.push(data.error);
        }

        if (data.detail) {
          errors.push(data.detail);
        }

        return errors.length > 0 ? errors.join("\n") : null;
      };

      const userMessage =
        formatFieldErrors(errorData) ||
        "Could not complete registration. Please try again.";

      throw new AppError({
        title: "Registration Error",
        response: error.response,
        userMessage,
      });
    }
  },
  async activate(uid, token) {
    try {
      const { data } = await axios.get(`/api/v3/auth/activate/${uid}/${token}`);
      return data;
    } catch (error) {
      throw new AppError({
        title: "Activation Error",
        response: error.response,
        userMessage:
          error.response?.data?.detail ||
          "Account activation failed. Please try again.",
      });
    }
  },
};

export default AuthService;
